import profileStore from "stores/profileStore";
import vaultStore from "stores/vaultStore";

// Same allowlist as utils/recoveryMigration.js — the only browser-persisted
// keys this app writes outside the auth cookie.
const STALE_SESSION_KEYS = ["account_type", "email", "chatHistory"];

/**
 * Drops any customer data scoped to the previous session: the in-memory
 * MobX profile/vault stores, the known sessionStorage keys, and the Apollo
 * cache. Call this on logout (so the next login starts clean) and right
 * after a successful login (so a previous user's leftover cache/session
 * data on the same tab can't bleed into the new session). Never touches the
 * auth cookie itself — that's managed separately via auth/token.js.
 *
 * Returns a promise — callers that continue on into the new session (e.g.
 * login navigating to the dashboard) must `await` it before doing so.
 * Otherwise a query fired by the new screen can resolve into the cache
 * before clearStore() finishes, and clearStore() then wipes that fresh data
 * out from under it.
 */
export const clearUserScopedState = async (apolloClient) => {
  STALE_SESSION_KEYS.forEach((key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore — sessionStorage may be unavailable in some environments
    }
  });

  profileStore.reset();
  vaultStore.reset();

  if (apolloClient) {
    try {
      // clearStore() empties the cache without refetching active queries —
      // components remount fresh (network-only, in effect) once the new
      // user's screens mount and issue their own queries.
      await apolloClient.clearStore();
    } catch {
      // best effort — proceed either way
    }
  }
};
